import AccountRegistration from './views/auth/pages/account-registration';
import BulletinsPage from './views/pages/resources/bulletins';
import { CacheBusterContextProvider } from './context/CacheBusterContext';
import { createBrowserHistory } from 'history';
import CreateCriticalOrganizationUpdates from './views/pages/system-admin/create-critical-organization-updates';
import CreateDistributionRule from './views/pages/system-admin/create-distribution-rule';
import CreditAppDoc from './views/pages/credit/documents';
import CreditAppPage from './views/pages/credit/application';
import CreditAppStatus from './views/pages/credit/status';
import Dashboard from './components/dashboard/index';
import DateAndTermComponent from './views/pages/system-admin/date-term-change';
import DealerActivation from './views/pages/system-admin/dealer-activation';
import DealerGroupMaintenance from './views/pages/system-admin/dealer-group-maintenance';
import DealerGroupMaintenanceAdd from './views/pages/system-admin/dealer-group-add';
import DealerGroupMaintenanceMove from './views/pages/system-admin/dealer-group-move';
import DealerGroupMaintenancePermissions from './views/pages/system-admin/dealer-group-permissions';
import DealerGroupMaintenanceSearch from './views/pages/system-admin/dealer-group-maintenance/table-data';
import DealerManagement from './views/pages/dealer-management';
import DealerRentalPage from './views/pages/dealer-rental';
import DealerSecurityDeleteUsers from './views/pages/system-admin/user-delete';
import DealerSecurityMaintenance from './views/pages/system-admin/dealer-security-maintenance';
import DealerSecurityMaintenanceUsers from './views/pages/system-admin/user-search';
import DealerSecurityMigrationUsers from './views/pages/system-admin/user-migration';
import DocumentsPage from './views/pages/resources/documents';
import EFTNotifications from './views/pages/funding/eft';
import FieldUserManagementComponent from './views/pages/system-admin/field-user-management';
import ForgotPassword from './views/auth/pages/forgotPassword';
import FundingReserves from './views/pages/funding/reserves';
import FundingStatus from './views/pages/funding/status';
import HomePage from './views/pages/home';
import InternalSecurityAdmin from './views/pages/system-admin/internal-security-maintenance';
import InternalSecurityMaintenanceUsers from './views/pages/system-admin/internal-security-maintenance/user-search';
import { IUser } from './utils/interfaces/user';
import LoginPage from '../src/views/auth/components/loginPage/LoginPage';
import PasswordReset from './views/auth/pages/passwordReset';
import { Permissions } from './utils/enums/permissions.enum';
import { PortfolioManagement } from './views/pages/portfolio-management';
import ProgramEligibility from './views/pages/program-eligibility';
import RegistrationSuccess from '../src/views/auth/pages/register-success';
import SearchVehicleInfo from './views/pages/system-admin/search-vehicle-info';
import ServiceLoanerPage from './views/pages/service-loaner';
import StaticPdfMaintenance from './views/pages/system-admin/staticPdfMaintenance';
import SystemAdminPage from './views/pages/system-admin';
import TermsAndConditions from './views/pages/credit/terms-and-conditions';
import TitleRequestPage from './views/pages/title-request';
import UpdateBulletin from './views/pages/system-admin/update-bulletin';
import UpdateCriticalOrganizationUpdates from './views/pages/system-admin/update-critical-organization-updates';
import UploadBulletin from './views/pages/system-admin/upload-bulletin';
import UploadVehicleInfo from './views/pages/system-admin/upload-vehicle-info';
import userStore from './store/userStore';
import ViewBulletin from './views/pages/system-admin/view-bulletin';
import ViewCriticalOrganizationUpdates from './views/pages/system-admin/view-critical-organization-updates';
import ViewDistributionRule from './views/pages/system-admin/view-distribution-rule';
import { useContext, useEffect } from 'react';

import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
    useLocation,
} from 'react-router-dom';
import {
    DetailsComponent,
    HistoryComponent,
    MakeComponent,
    PendingComponent,
} from './views/pages/payments';
import { getPermissionsForPath, isRestrictedSystemAdminRoute } from './routes';
import {
    getPermissionsMap,
    isAllow,
    isSystemAdminUIEnabled,
    isValidDealerPortal,
} from './utils/helpers';
import {
    MaintenanceContext,
    MaintenanceContextProvider,
} from './context/MaintenanceContext';

import {
    PayoffQuoteComponent,
    PayoffQuoteDetailComponent,
} from './views/pages/payoff';
import { Provider, useSelector } from 'react-redux';
import './assets/styles/main.scss';
import './localization/i18n';

function PrivateRoute({ children }: any) {
    const history = createBrowserHistory();

    const {
        cognitoToken,
        permissionsToken,
        currentDealers,
        dealerCountry,
        accessUser,
    } = useSelector((state: IUser) => state);

    const location = useLocation();
    const permissionNeeded = getPermissionsForPath(location.pathname);
    const permissionsMap = getPermissionsMap();

    const hasInvalidDealerPortal =
        dealerCountry !== null && !isValidDealerPortal(dealerCountry);

    // The field user shortcut must not open pages that have their own permission.
    const isFieldUserSystemAdminAllowed =
        location.pathname.startsWith('/dashboard/systemadmin') &&
        !isRestrictedSystemAdminRoute(location.pathname) &&
        Boolean(accessUser) &&
        isAllow(
            [Permissions.DEALER_SECURITY_MAINTENANCE],
            permissionsMap,
            currentDealers?.default,
        );

    const doesNotHaveRoutePermission =
        permissionNeeded &&
        !isAllow(permissionNeeded, permissionsMap, currentDealers?.default) &&
        !isFieldUserSystemAdminAllowed;

    if (
        (!cognitoToken && !permissionsToken) ||
        hasInvalidDealerPortal ||
        doesNotHaveRoutePermission
    ) {
        return <Navigate to='/login' state={{ from: history.location }} />;
    }

    return children;
}

function MaintenanceGuard({ children }: any) {
    const { maintenanceMode, fetchMaintenance }: any =
        useContext(MaintenanceContext);
    const location = useLocation();
    const history = createBrowserHistory();

    useEffect(() => {
        fetchMaintenance();
    }, [location]);

    if (maintenanceMode) {
        return <Navigate to='/login' state={{ from: history.location }} />;
    }
    return children;
}

function App() {
    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route>
                <Route path='/'>
                    <Route index element={<LoginPage />} />
                    <Route path={'login'} element={<LoginPage />} />
                </Route>
                <Route
                    path='/registration-success'
                    element={<RegistrationSuccess />}
                />
                <Route
                    path={'/account-registration'}
                    element={
                        <MaintenanceGuard>
                            <AccountRegistration />
                        </MaintenanceGuard>
                    }
                />
                <Route
                    path={'/passwordReset'}
                    element={
                        <MaintenanceGuard>
                            <PasswordReset />
                        </MaintenanceGuard>
                    }
                />
                <Route
                    path={'/forgotPassword'}
                    element={
                        <MaintenanceGuard>
                            <ForgotPassword />
                        </MaintenanceGuard>
                    }
                />
                <Route
                    path='/terms-and-conditions'
                    element={
                        <MaintenanceGuard>
                            <TermsAndConditions />
                        </MaintenanceGuard>
                    }
                />
                <Route
                    path='/dashboard'
                    element={
                        <MaintenanceGuard>
                            <PrivateRoute>
                                <Dashboard />
                            </PrivateRoute>
                        </MaintenanceGuard>
                    }
                >
                    <Route index element={<HomePage />} />
                    <Route path={'credit'}>
                        <Route
                            path={'application'}
                            element={<CreditAppPage />}
                        />
                        <Route path={'status'} element={<CreditAppStatus />} />
                        <Route path={'documents'} element={<CreditAppDoc />} />
                    </Route>
                    <Route path={'funding'}>
                        <Route path={'status'} element={<FundingStatus />} />
                        <Route path={'eft'} element={<EFTNotifications />} />
                        <Route
                            path={'reserves'}
                            element={<FundingReserves />}
                        />
                    </Route>
                    <Route path={'resources'}>
                        <Route path={'documents'} element={<DocumentsPage />} />
                        <Route path={'bulletins'} element={<BulletinsPage />} />
                    </Route>
                    <Route path={'payments'}>
                        <Route path={'make'} element={<MakeComponent />} />
                        <Route
                            path={'details'}
                            element={<DetailsComponent />}
                        />
                        <Route
                            path={'pending'}
                            element={<PendingComponent />}
                        />
                        <Route
                            path={'history'}
                            element={<HistoryComponent />}
                        />
                    </Route>
                    <Route path={''}>
                        <Route
                            path={'payoff-quotes'}
                            element={<PayoffQuoteComponent />}
                        />
                        <Route
                            path={'title-request'}
                            element={<TitleRequestPage />}
                        />
                    </Route>
                    <Route
                        path={'payoff-quotes/details'}
                        element={<PayoffQuoteDetailComponent />}
                    />
                    <Route
                        path={'portfolio-management'}
                        element={<PortfolioManagement />}
                    />
                    <Route
                        path={'program-eligibility'}
                        element={<ProgramEligibility />}
                    />
                    <Route
                        path={'dealer-management'}
                        element={<DealerManagement />}
                    />
                    <Route
                        path='serviceLoaner'
                        element={<ServiceLoanerPage />}
                    />
                    <Route path='dealerRental' element={<DealerRentalPage />} />
                    {isSystemAdminUIEnabled() && (
                        <>
                            <Route
                                path='systemadmin'
                                element={<SystemAdminPage />}
                            />
                            <Route
                                path='systemadmin/dealer-activation'
                                element={<DealerActivation />}
                            />
                            <Route
                                path='systemadmin/static-pdf-maintenance'
                                element={<StaticPdfMaintenance />}
                            />
                            <Route
                                path='systemadmin/create-distribution-rule'
                                element={<CreateDistributionRule />}
                            />
                            <Route
                                path='systemadmin/edit-distribution-rule'
                                element={<CreateDistributionRule />}
                            />
                            <Route
                                path='systemadmin/view-distribution-rule'
                                element={<ViewDistributionRule />}
                            />
                            <Route
                                path='systemadmin/upload-bulletin'
                                element={<UploadBulletin />}
                            />
                            <Route
                                path='systemadmin/view-bulletin'
                                element={<ViewBulletin />}
                            />
                            <Route
                                path='systemadmin/update-bulletin'
                                element={<UpdateBulletin />}
                            />
                            <Route
                                path='systemadmin/create-critical-organization-updates'
                                element={<CreateCriticalOrganizationUpdates />}
                            />
                            <Route
                                path='systemadmin/view-critical-organization-updates'
                                element={<ViewCriticalOrganizationUpdates />}
                            />
                            <Route
                                path='systemadmin/update-critical-organization-updates'
                                element={<UpdateCriticalOrganizationUpdates />}
                            />
                            <Route
                                path='systemadmin/upload-vehicle-info'
                                element={<UploadVehicleInfo />}
                            />
                            <Route
                                path='systemadmin/search-vehicle-info'
                                element={<SearchVehicleInfo />}
                            />
                            <Route
                                path='systemadmin/dealer-security-maintenance'
                                element={<DealerSecurityMaintenance />}
                            />
                            <Route
                                path='systemadmin/dealer-security-maintenance/search'
                                element={<DealerSecurityMaintenanceUsers />}
                            />
                            <Route
                                path='systemadmin/dealer-security-maintenance/delete'
                                element={<DealerSecurityDeleteUsers />}
                            />
                            <Route
                                path='systemadmin/dealer-security-maintenance/migrate'
                                element={<DealerSecurityMigrationUsers />}
                            />
                            <Route
                                path='systemadmin/dealer-group-maintenance'
                                element={<DealerGroupMaintenance />}
                            />
                            <Route
                                path='systemadmin/dealer-group-maintenance/search'
                                element={<DealerGroupMaintenanceSearch />}
                            />
                            <Route
                                path='systemadmin/dealer-group-maintenance/permissions'
                                element={<DealerGroupMaintenancePermissions />}
                            />
                            <Route
                                path='systemadmin/dealer-group-maintenance/add'
                                element={<DealerGroupMaintenanceAdd />}
                            />
                            <Route
                                path='systemadmin/dealer-group-maintenance/move'
                                element={<DealerGroupMaintenanceMove />}
                            />
                            <Route
                                path='systemadmin/field-user-management'
                                element={<FieldUserManagementComponent />}
                            />
                            <Route
                                path='systemadmin/internal-security-maintenance'
                                element={<InternalSecurityAdmin />}
                            />
                            <Route
                                path='systemadmin/internal-security-maintenance/search'
                                element={<InternalSecurityMaintenanceUsers />}
                            />
                            <Route
                                path='systemadmin/date-term-change'
                                element={<DateAndTermComponent />}
                            />
                        </>
                    )}
                </Route>
            </Route>,
        ),
    );
    return (
        <CacheBusterContextProvider>
            <MaintenanceContextProvider>
                <Provider store={userStore}>
                    <RouterProvider router={router} />
                </Provider>
            </MaintenanceContextProvider>
        </CacheBusterContextProvider>
    );
}

export default App;
